var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var AmazonS3ImportConfig_1;
import { JSON, JsonDiscriminatorValue, JsonSchemaV2 } from '@symon-ai/wisepipe-serialize';
import { ImportOperation } from '../importTask';
import { ImportConfig, ImportConfigType } from './importConfig';
import './dataPreprocessConfig';
import { amazonS3ImportConfigSchemaValidation } from '../../validations.generated';
export var AmazonS3SupportedType;
(function (AmazonS3SupportedType) {
    AmazonS3SupportedType["Csv"] = ".csv";
    AmazonS3SupportedType["Excel"] = ".xlsx|.xls";
    AmazonS3SupportedType["Text"] = ".txt";
})(AmazonS3SupportedType || (AmazonS3SupportedType = {}));
export const ExcelMaxFileSizeBytes = 2 * 1024 * 1024 * 1024; // 2 GB
export const amazonS3ImportConfigSchema = {
    $id: 'urn:wisepipe-common-data:AmazonS3ImportConfig',
    type: 'object',
    properties: {
        filename: { type: 'string', minLength: 1 },
        delimiter: { type: 'string', minLength: 1, maxLength: 1 },
        quotechar: { type: 'string', minLength: 1, maxLength: 1 },
        filetype: {
            type: 'string',
            enum: Object.values(AmazonS3SupportedType)
        },
        prefix: { type: 'string' },
        hasHeader: { type: 'boolean' },
        dataPreprocessConfig: { $ref: 'urn:wisepipe-common-data:DataPreprocessConfig' }
    },
    // delimiter is required for Csv file types
    if: {
        properties: { filetype: { enum: [AmazonS3SupportedType.Csv, AmazonS3SupportedType.Text] } },
        required: ['filetype']
    },
    then: { required: ['delimiter'] },
    required: ['filename', 'filetype']
};
let AmazonS3ImportConfig = AmazonS3ImportConfig_1 = class AmazonS3ImportConfig extends ImportConfig {
    constructor(filename, filetype, delimiter, quotechar, prefix, hasHeader, dataPreprocessConfig, columnsToUpdate) {
        super(ImportConfigType.AmazonS3, columnsToUpdate);
        this.filename = filename;
        this.filetype = filetype;
        this.delimiter = delimiter;
        this.quotechar = quotechar;
        this.prefix = prefix;
        this.hasHeader = hasHeader;
        this.dataPreprocessConfig = dataPreprocessConfig;
        JSON.validate(this, AmazonS3ImportConfig_1);
    }
    get supportedRefreshOperations() {
        return [ImportOperation.Overwrite];
    }
    get needsDiscovery() {
        return true;
    }
    get fileTypeSupportsTargetParquet() {
        return this.isCsv();
    }
    get fileTypeSupportsDataPreprocess() {
        return this.isCsv();
    }
    get isFileTypeCsv() {
        return this.isCsv();
    }
    get tableInfo() {
        /*
            This config can support selecting multiple files at the same time if they match a regex defined for search_pattern property
            If multiple files are found that match the pattern (and they all share the same column names), the rows from all files will
            be combined into a single output file. This can be used to extend the S3 connector's functionality to allow for multiple files combining
            into a single file during import.
            
            'key_properties' defines the primary key of the files, to deduplicate rows with the same primary key if multiple files are combined

            'date_overrides' can be supplied as the column names that should be imported as dates. Not sure if that is necessary for our purposes

            {
                "search_prefix": "exports"
                "search_pattern": "my_table\\/.*\\.csv",
                "table_name": "my_table",
                "key_properties": ["id"],
                "date_overrides": "created_at",
                "delimiter": ","
            }
        */
        const config = {
            search_prefix: this.prefix,
            search_pattern: this.isCsv() ? ImportConfig.escapeStringRegexp(this.filename) : this.filename, // This will be treated as a regex pattern in tap-s3-csv, so escape it.
            table_name: AmazonS3ImportConfig_1.cleanTableName(this.filename),
            delimiter: this.delimiter,
            quotechar: this.quotechar,
            recursive_search: false, // tap-s3-csv will search the bucket recursively by default. For now, prevent recursive searches in case multiple files in the same folder hierarchy have the same name
            has_header: this.hasHeader
        };
        if (this.isExcel()) {
            config['size_threshold'] = ExcelMaxFileSizeBytes;
        }
        return [config];
    }
    isCsv() {
        return [AmazonS3SupportedType.Csv, AmazonS3SupportedType.Text].includes(this.filetype);
    }
    isExcel() {
        return this.filetype === AmazonS3SupportedType.Excel;
    }
    static cleanTableName(filename) {
        // Keep the safe characters as described here: https://docs.aws.amazon.com/AmazonS3/latest/userguide/object-keys.html, change all others to a dash
        return filename.replace(/[^a-z0-9\/!\-_\.*'\(\) ]/gi, '-');
    }
    // awss3 just uses tap-csv/excel so it needs to clean special formatting
    get shouldTargetCleanData() {
        return true;
    }
};
AmazonS3ImportConfig = AmazonS3ImportConfig_1 = __decorate([
    JsonSchemaV2(amazonS3ImportConfigSchema, amazonS3ImportConfigSchemaValidation),
    JsonDiscriminatorValue(ImportConfigType.AmazonS3)
], AmazonS3ImportConfig);
export { AmazonS3ImportConfig };
//# sourceMappingURL=amazonS3ImportConfig.js.map